import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getAvatarUrl, type AvatarKey } from '@/constants/avatars';
import { GRADE_LABELS, type ChildGrade } from '@/hooks/useChild';
import { ChevronDown, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface ChildProfileHeaderProps {
  /** Child nickname */
  nickname: string;
  /** Avatar key */
  avatar: AvatarKey;
  /** Grade */
  grade: ChildGrade;
  /** Whether multi-child switching is available */
  canSwitch?: boolean;
  /** Callback when pressed (for switching children) */
  onPress?: () => void;
  /** Whether sidebar is collapsed */
  collapsed?: boolean;
  /** Callback to toggle sidebar */
  onToggleSidebar?: () => void;
  /** Additional class names */
  className?: string;
}

/**
 * ChildProfileHeader - Sidebar top child info section
 *
 * ChatGPT style design:
 * - Display current child avatar, name, grade
 * - Support tap to switch child (if multiple)
 * - Collapse button integrated on the right
 * - Clean card style
 */
export function ChildProfileHeader({
  nickname,
  avatar,
  grade,
  canSwitch = false,
  onPress,
  collapsed = false,
  onToggleSidebar,
  className,
}: ChildProfileHeaderProps) {
  const { t } = useTranslation();
  const gradeLabel = GRADE_LABELS[grade] || grade;

  // Collapsed state: only show avatar and toggle button
  if (collapsed) {
    return (
      <div
        className={cn('flex flex-col items-center gap-2 border-b border-gray-100 py-4', className)}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={onPress} className="rounded-full transition-transform hover:scale-105">
              <Avatar className="h-10 w-10 bg-violet-100">
                <AvatarImage src={getAvatarUrl(avatar)} alt={nickname} />
                <AvatarFallback className="bg-violet-100 text-violet-700">
                  {nickname.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="font-medium">{nickname}</p>
            <p className="text-xs text-gray-400">{gradeLabel}</p>
          </TooltipContent>
        </Tooltip>

        {onToggleSidebar && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onToggleSidebar}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <PanelLeft className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{t('sidebar:actions.expand')}</TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  }

  // Expanded state: full info with switch and toggle
  return (
    <div className={cn('flex items-center gap-3 border-b border-gray-100 px-4 py-4', className)}>
      {/* Clickable area: Avatar + Info + Switch */}
      <button
        onClick={onPress}
        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-colors"
      >
        {/* Avatar */}
        <Avatar className="h-11 w-11 shrink-0 bg-violet-100">
          <AvatarImage src={getAvatarUrl(avatar)} alt={nickname} />
          <AvatarFallback className="bg-violet-100 text-violet-700">
            {nickname.charAt(0)}
          </AvatarFallback>
        </Avatar>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-gray-900">{nickname}</p>
          <p className="text-sm text-gray-500">{gradeLabel}</p>
        </div>

        {/* Switch indicator */}
        {canSwitch && (
          <div className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-gray-400 transition-colors hover:bg-gray-100">
            <span className="text-xs">{t('profile:child.switch')}</span>
            <ChevronDown className="h-3.5 w-3.5" />
          </div>
        )}
      </button>

      {/* Toggle Sidebar Button - separate from clickable area */}
      {onToggleSidebar && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onToggleSidebar}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <PanelLeftClose className="h-5 w-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{t('sidebar:actions.collapse')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
